import { useContext, useMemo, useRef, useState } from "react";
import { Box } from "@mui/material";
import QRCode from "react-fancy-qrcode";
import html2canvas from "html2canvas";
import LogoRupin from "../../../assets/icons/Logo Rupin.svg";
import { CopyIconNew } from "../../../assets/icons/icon";
import { ParagraphText, UBButton } from "../../../Libraries";
import { LanguageContext } from "../../../context/LanguageContext";
import { useSnackbar } from "../../../context/SnackbarContext";
import { formatNum } from "../../../oscar-pos-core/constants";
import { analytics, triggerLogEvent } from "../../../config/firebase";
import {
  getQrCanvasFromElement,
  downloadCanvasAsPng,
} from "../../../utils/qrCodeCanvas";
import styles from "./styles.module.css";

const TAB_SCAN_QR = "scan_qr";
const TAB_BANK_TRANSFER = "bank_transfer";

const ScanAndPay = ({
  amount = 0,
  amountLabel = "",
  dueDate = "",
  billerName = "",
  billerLogo = "",
  consumerName = "",
  consumerNumber = "",
  iban = "",
  accountTitle = "",
  qrValue = "",
  className = "",
  onVerifyPayment = () => {},
  isVerifying = false,
  hideToggle = false,
}) => {
  const [I18n] = useContext(LanguageContext);
  const { showSnackbar } = useSnackbar();
  const [activeTab, setActiveTab] = useState(TAB_SCAN_QR);
  const qrFrameRef = useRef(null);
  const effectiveHideToggle = hideToggle || !qrValue;
  const effectiveTab = effectiveHideToggle ? TAB_BANK_TRANSFER : activeTab;

  const formattedAmount = formatNum(amount || 0);
  const metaText = [
    consumerName,
    consumerNumber ? `${I18n.id} ${consumerNumber}` : "",
    dueDate ? `${I18n.due} ${dueDate}` : "",
  ]
    .filter(Boolean)
    .join(" · ");
  const computedAccountTitle =
    accountTitle ||
    (billerName
      ? `Rupin Bill Collections - ${billerName}`
      : "Rupin Bill Collections");

  const copyTextFallback = (text) => {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    let copied = false;
    try {
      copied = document.execCommand("copy");
    } catch {
      copied = false;
    }
    document.body.removeChild(textarea);
    return copied;
  };

  const handleCopyIban = async () => {
    let copied = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(iban);
        copied = true;
      }
    } catch {
      copied = false;
    }
    if (!copied) copied = copyTextFallback(iban);

    if (copied) {
      showSnackbar({
        message: I18n.copied,
        severity: "success",
      });
      triggerLogEvent(analytics, "web_bill_iban_copied", {
        biller: billerName,
      });
    } else {
      showSnackbar({
      message: I18n.failed_to_copy,
        severity: "error",
      });
    }
  };

  const handleDownloadQr = async () => {
    triggerLogEvent(analytics, "web_bill_payment_download_qr", {
      biller: billerName,
    });
    if (!qrFrameRef.current) return;
    try {
      const canvas = await getQrCanvasFromElement(qrFrameRef.current);
      downloadCanvasAsPng(canvas);
    } catch {
      try {
        const canvas = await html2canvas(qrFrameRef.current);
        downloadCanvasAsPng(canvas);
      } catch {
        showSnackbar({ message: I18n.failed_to_copy, severity: "error" });
      }
    }
  };

  const handleVerifyPayment = () => {
    triggerLogEvent(analytics, "web_bill_payment_verify_payment_click", {
      biller: billerName,
      method: effectiveTab,
    });
    onVerifyPayment();
  };

  const handleTabChange = (tab) => {
    if (tab === activeTab) return;
    setActiveTab(tab);
    triggerLogEvent(analytics, "web_bill_payment_tab_change", { tab });
  };

  const isScanActive = effectiveTab === TAB_SCAN_QR;

  const scanStep2Text = useMemo(() => {
    const template =
      I18n.scan_step_2 || "Confirm the amount, Rs. {amount} and transfer";
    return template.replace("{amount}", formattedAmount);
  }, [I18n.scan_step_2, formattedAmount]);

  const bankStep2Text = useMemo(() => {
    const template =
      I18n.bank_step_2 ||
      "Transfer the exact amount Rs. {amount} to the IBAN above";
    return template.replace("{amount}", formattedAmount);
  }, [I18n.bank_step_2, formattedAmount]);

  return (
    <Box className={`${styles.container} ${className}`}>
      <Box className={styles.mainCard}>
        <Box className={styles.summarySection}>
          <ParagraphText className={styles.summaryLabel}>
            {amountLabel || I18n.total_balance_due}
          </ParagraphText>
          <ParagraphText className={styles.amountValue}>
            Rs. {formattedAmount}
          </ParagraphText>
          {metaText ? (
            <Box className={styles.metaLine}>
              {billerLogo ? (
                <img src={billerLogo} alt="" className={styles.metaAvatar} />
              ) : null}
              <ParagraphText className={styles.metaText}>
                {metaText}
              </ParagraphText>
            </Box>
          ) : null}
        </Box>

        {!effectiveHideToggle ? (
          <Box className={styles.tabsWrapper}>
            <Box className={styles.tabsPill}>
              <span
                className={`${styles.tabIndicator} ${
                  !isScanActive ? styles.tabIndicatorRight : ""
                }`}
                aria-hidden="true"
              />
              <button
                type="button"
                className={`${styles.tabBtn} ${
                  isScanActive ? styles.tabBtnActive : ""
                }`}
                onClick={() => handleTabChange(TAB_SCAN_QR)}
              >
                {I18n.scan_qr}
              </button>
              <button
                type="button"
                className={`${styles.tabBtn} ${
                  !isScanActive ? styles.tabBtnActive : ""
                }`}
                onClick={() => handleTabChange(TAB_BANK_TRANSFER)}
              >
                {I18n.bank_transfer}
              </button>
            </Box>
          </Box>
        ) : null}

        <Box className={styles.contentArea}>
          <Box
            className={`${styles.tabPanel} ${
              isScanActive ? styles.tabPanelVisible : styles.tabPanelHidden
            }`}
          >
            <Box className={styles.qrSection}>
              <Box className={styles.qrCodeFrame} ref={qrFrameRef}>
                {qrValue ? (
                  <QRCode
                    value={qrValue}
                    size={180}
                    dotRadius="12%"
                    dotScale={1}
                    positionRadius={5}
                    logo={LogoRupin}
                  />
                ) : null}
              </Box>
            </Box>
            <ParagraphText
              className={styles.downloadLink}
              onClick={handleDownloadQr}
            >
              {I18n.download_qr}
            </ParagraphText>
          </Box>

          <Box
            className={`${styles.tabPanel} ${
              !isScanActive ? styles.tabPanelVisible : styles.tabPanelHidden
            }`}
          >
            <Box className={styles.bankSection}>
              <ParagraphText className={styles.ibanLabel}>
                {I18n.transfer_to_this_iban}
              </ParagraphText>
              <Box className={styles.ibanBox}>
                <ParagraphText className={styles.ibanText}>
                  {iban}
                </ParagraphText>
                <button
                  type="button"
                  className={styles.copyBtn}
                  onClick={handleCopyIban}
                  aria-label={I18n.copy}
                >
                  <CopyIconNew width={20} height={20} />
                </button>
              </Box>

              <ParagraphText className={styles.bankTitle}>
                {I18n.bank_name}:{" "}
                <span className={styles.accountTitleInner}> {I18n.rupin} </span>
              </ParagraphText>

              <ParagraphText className={styles.accountTitle}>
                {I18n.account_title}:{" "}
                <span className={styles.accountTitleInner}>
                  {" "}
                  {computedAccountTitle}{" "}
                </span>
              </ParagraphText>
              <ParagraphText className={styles.exactAmountNote}>
                {I18n.transfer_exact_amount_shown_above}
              </ParagraphText>
            </Box>
          </Box>
        </Box>

        <UBButton
          id="web_bill_payment_verify_payment_btn"
          title={I18n.verify_payment}
          className={`ubPrimaryButton textUppercase ${styles.verifyButton}`}
          onClick={handleVerifyPayment}
          loading={isVerifying}
          disabled={isVerifying}
        />
      </Box>

      <Box className={styles.instructionCard}>
        <ParagraphText className={styles.instructionTitle}>
          {I18n.how_to_pay}
        </ParagraphText>
        <ul className={styles.stepsList}>
          {isScanActive ? (
            <>
              <li className={styles.stepItem}>1. {I18n.scan_step_1}</li>
              <li className={styles.stepItem}>2. {scanStep2Text}</li>
              <li className={styles.stepItem}>
                3. {I18n.scan_step_3 || I18n.step_click_pay}
              </li>
            </>
          ) : (
            <>
              <li className={styles.stepItem}>1. {I18n.bank_step_1}</li>
              <li className={styles.stepItem}>2. {bankStep2Text}</li>
              <li className={styles.stepItem}>
                3. {I18n.bank_step_3 || I18n.step_click_pay}
              </li>
            </>
          )}
        </ul>
      </Box>
    </Box>
  );
};

export default ScanAndPay;
